/** Tiny date helpers used by the dashboard. Kept pure for easy testing. */

export function greetingForHour(hour: number): "Good morning" | "Good afternoon" | "Good evening" {
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
}

const MARKET_OPEN_HOUR = 9;
const MARKET_OPEN_MIN = 15;
const MARKET_CLOSE_HOUR = 15;
const MARKET_CLOSE_MIN = 30;

export type MarketPhase = "pre-market" | "open" | "closed";

export function marketPhaseAt(date: Date): MarketPhase {
  const minutes = date.getHours() * 60 + date.getMinutes();
  const open = MARKET_OPEN_HOUR * 60 + MARKET_OPEN_MIN;
  const close = MARKET_CLOSE_HOUR * 60 + MARKET_CLOSE_MIN;
  if (minutes < open) return "pre-market";
  if (minutes >= close) return "closed";
  return "open";
}

export function formatDateLong(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export function formatClockHHmm(date: Date): string {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

export function minutesUntil(date: Date, targetHour: number, targetMin: number): number {
  const now = date.getHours() * 60 + date.getMinutes();
  const target = targetHour * 60 + targetMin;
  return target - now;
}

export function describeTimeToOpen(date: Date): string | null {
  const phase = marketPhaseAt(date);
  if (phase !== "pre-market") return null;
  const mins = minutesUntil(date, MARKET_OPEN_HOUR, MARKET_OPEN_MIN);
  if (mins <= 0) return null;
  if (mins < 60) return `Market opens in ${String(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `Market opens in ${String(h)}h ${String(m).padStart(2, "0")}m`;
}

export function describeTimeToSquareOff(date: Date): string | null {
  const phase = marketPhaseAt(date);
  if (phase !== "open") return null;
  const SQUARE_OFF_HOUR = 15;
  const SQUARE_OFF_MIN = 24;
  const mins = minutesUntil(date, SQUARE_OFF_HOUR, SQUARE_OFF_MIN);
  if (mins <= 0) return null;
  if (mins < 60) return `${String(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h)}h ${String(m).padStart(2, "0")}m`;
}
